"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Activity,
  CalendarCheck,
  ChevronDown,
  Globe,
  HardHat,
  KanbanSquare,
  LogOut,
  Package,
  Settings,
  Users,
  Warehouse,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logoutAction } from "@/actions/auth";
import { roleLabel } from "@/lib/roles";
import NotificationBell from "@/components/notification-bell";
import { ErrorBoundary } from "@/components/error-boundary";
import { useThemeToggle } from "@/components/theme-toggle";
import { initials } from "@/components/cards/employee-avatars";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type NavItem = { name: string; href: string; icon: LucideIcon };

const BASE_ITEMS: NavItem[] = [
  { name: "Доска", href: "/board", icon: KanbanSquare },
  { name: "Площадка", href: "/floor", icon: Warehouse },
  { name: "Расписание", href: "/schedule", icon: CalendarCheck },
  { name: "Клиенты", href: "/clients", icon: Users },
  { name: "Сотрудники", href: "/workers", icon: HardHat },
  { name: "Заказ запчастей", href: "/parts", icon: Package },
];
const SETTINGS_ITEM: NavItem = { name: "Настройки", href: "/settings", icon: Settings };
const SUPERUSER_ITEMS: NavItem[] = [
  { name: "Сайты", href: "/websites", icon: Globe },
  SETTINGS_ITEM,
  { name: "Мониторинг", href: "/monitoring", icon: Activity },
];
const TECH_ITEMS: NavItem[] = [{ name: "Мои задачи", href: "/tech", icon: Wrench }];

function itemsFor(role?: string): NavItem[] {
  if (role === "service-tech") return TECH_ITEMS;
  if (role === "superuser") return [...BASE_ITEMS, ...SUPERUSER_ITEMS];
  if (role === "manager") return [...BASE_ITEMS, SETTINGS_ITEM];
  return BASE_ITEMS;
}

/**
 * Left navigation in the esal ERP style: sits directly on the canvas (no
 * panel of its own), icon + label rows, the active row marked by a soft blue
 * wash and a thin bar on its leading edge, and the user menu at the bottom.
 */
export function Sidebar({ role, username, userId }: { role?: string; username?: string; userId?: string }) {
  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between px-4 pt-5 pb-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#2A98D9] to-[#6452F7] text-[11px] font-bold tracking-wide text-white shadow-sm">
            TSE
          </div>
          <div className="text-muted-foreground min-w-0 truncate text-[11px]">Сервисный центр</div>
        </div>
        {userId && (
          <ErrorBoundary>
            <NotificationBell userId={userId} />
          </ErrorBoundary>
        )}
      </div>

      <nav className="nav-scroll flex-1 overflow-y-auto px-2 pb-2">
        <MenuItems role={role} />
      </nav>

      <div className="mt-auto px-2 pb-3">
        <UserMenu username={username} role={role} />
      </div>
    </div>
  );
}

export function MenuItems({ role, onNavigate }: { role?: string; onNavigate?: () => void }) {
  const pathname = usePathname();
  return (
    <div className="space-y-0.5">
      {itemsFor(role).map((item) => {
        const active = pathname === item.href || pathname.startsWith(item.href + "/");
        const Icon = item.icon;
        return (
          <Link
            key={item.href}
            href={item.href}
            onClick={onNavigate}
            className={cn(
              "relative isolate flex w-full items-center gap-3 overflow-hidden rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150",
              active ? "text-primary dark:text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {active && (
              <>
                <span
                  aria-hidden
                  className="animate-in fade-in-0 slide-in-from-left-3 pointer-events-none absolute inset-0 -z-10 rounded-md bg-gradient-to-r from-[#2A98D9]/15 via-[#2A98D9]/5 to-transparent duration-300 ease-out"
                />
                <span
                  aria-hidden
                  className="animate-in fade-in-0 zoom-in-50 bg-primary pointer-events-none absolute inset-y-0 left-0 -z-10 w-[2.5px] rounded-sm duration-500 ease-out"
                />
              </>
            )}
            <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
            <span className="flex-1 truncate">{item.name}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function UserMenu({ username, role }: { username?: string; role?: string }) {
  const theme = useThemeToggle();
  const name = username || "—";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="hover:bg-muted flex w-full items-center gap-3 rounded-md p-2 transition-colors duration-150 focus:outline-none"
          aria-label="Меню пользователя"
        >
          <span className="relative shrink-0">
            <span className="bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold">
              {initials(name)}
            </span>
            <span className="pointer-events-none absolute -right-1 -bottom-1 h-4 w-4 rounded-full bg-gradient-to-br from-[#A1CDED] to-[#B6AFFD]" />
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="text-foreground block truncate text-sm font-semibold">{name}</span>
            <span className="text-muted-foreground block truncate text-xs">{roleLabel(role)}</span>
          </span>
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="border-border bg-card w-56 rounded-lg border p-1.5 shadow-lg">
        <DropdownMenuLabel className="text-muted-foreground px-2 py-1.5 text-xs">
          Вы вошли как <span className="text-foreground font-semibold">{name}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer rounded-md px-2 py-2 text-sm" onSelect={(e) => { e.preventDefault(); theme.toggle(); }}>
          <theme.Icon className="text-muted-foreground mr-2 h-4 w-4" />
          {theme.label}
        </DropdownMenuItem>
        <DropdownMenuItem className="text-destructive focus:text-destructive cursor-pointer rounded-md px-2 py-2 text-sm" onSelect={() => logoutAction()}>
          <LogOut className="mr-2 h-4 w-4" />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
